import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiFetch } from '../../lib/api';
import type { SavedAvatarData } from '../../lib/api';
import { AUTH_QUERY_KEY, useAuth } from '../auth/useAuth';
import {
  useActivateAvatar,
  useDeleteAvatar,
  useToggleAvatarLike,
} from './useAvatarCollection';
import AvatarCanvas from '../avatar/AvatarCanvas';
import type { AvatarConfig } from '../avatar/avatarTypes';
import './gallery.css';

export default function AvatarDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: user } = useAuth();

  const avatarId = Number(id);

  const { data: avatar, isLoading } = useQuery<SavedAvatarData>({
    queryKey: ['avatars', 'detail', avatarId],
    queryFn: () => apiFetch<SavedAvatarData>(`/api/avatars/${avatarId}/`),
    enabled: !isNaN(avatarId),
  });

  const activate = useActivateAvatar();
  const remove = useDeleteAvatar();
  const toggleLike = useToggleAvatarLike();

  const isOwner = user?.username === avatar?.owner;

  const handleActivate = () => {
    if (!avatar) return;
    activate.mutate(avatar.id, {
      onSuccess: () => {
        toast.success('Avatar activated!');
        qc.invalidateQueries({ queryKey: ['avatars', 'detail', avatarId] });
        qc.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      },
      onError: () => toast.error('Failed to activate'),
    });
  };

  const handleDelete = () => {
    if (!avatar || !confirm('Delete this avatar?')) return;
    remove.mutate(avatar.id, {
      onSuccess: () => {
        toast.success('Avatar deleted');
        navigate('/avatars');
      },
      onError: () => toast.error('Failed to delete'),
    });
  };

  const handleLike = () => {
    if (!avatar) return;
    toggleLike.mutate(avatar.id, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['avatars', 'detail', avatarId] });
      },
    });
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/avatars/${avatarId}`;
    await navigator.clipboard.writeText(url);
    toast.success('Link copied!');
  };

  const handleFork = () => {
    if (!avatar) return;
    navigate(`/avatar?fork=${avatar.id}`);
  };

  const handleEdit = () => {
    if (!avatar) return;
    navigate(`/avatar?edit=${avatar.id}`);
  };

  if (isLoading) {
    return (
      <div className="malpedia-gallery-page">
        <div className="malpedia-gallery-loading">Loading...</div>
      </div>
    );
  }

  if (!avatar) {
    return (
      <div className="malpedia-gallery-page">
        <div className="malpedia-gallery-loading">Avatar not found</div>
      </div>
    );
  }

  return (
    <div className="malpedia-gallery-page">
      <div className="malpedia-gallery-header">
        <div className="malpedia-gallery-header-left">
          <button
            className="malpedia-avatar-btn"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>
      </div>

      <div className="malpedia-avatar-detail">
        <div className="malpedia-avatar-detail-canvas">
          <AvatarCanvas
            config={avatar.config as unknown as AvatarConfig}
            pixelScale={16}
          />
        </div>

        <h2 className="malpedia-avatar-detail-name">{avatar.name}</h2>

        <span className="malpedia-avatar-detail-owner">
          by <Link to={`/users/${avatar.owner_id}`}>{avatar.owner}</Link>
        </span>

        <div className="malpedia-avatar-card-meta">
          {avatar.is_active && (
            <span className="malpedia-avatar-card-badge">Active</span>
          )}
          <button
            className={`malpedia-like-btn${avatar.user_liked ? ' malpedia-like-btn--liked' : ''}`}
            onClick={handleLike}
          >
            {avatar.user_liked ? '\u2764\uFE0F' : '\u2661'} {avatar.like_count}
          </button>
        </div>

        <div className="malpedia-avatar-detail-actions">
          <button className="malpedia-avatar-btn" onClick={handleShare}>
            Share
          </button>
          <button className="malpedia-avatar-btn" onClick={handleFork}>
            Use as starting point
          </button>
          {isOwner && (
            <>
              <button className="malpedia-avatar-btn" onClick={handleEdit}>
                Edit
              </button>
              {!avatar.is_active && (
                <button
                  className="malpedia-avatar-btn malpedia-avatar-btn--primary"
                  onClick={handleActivate}
                >
                  Activate
                </button>
              )}
              <button
                className="malpedia-avatar-btn"
                style={{ color: '#e64553' }}
                onClick={handleDelete}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
